import React, {createRef, useState, useContext} from 'react';
import { useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import { TablePagination, Checkbox, Select, MenuItem, useTheme } from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import FlightCrewsValidator from "../validators/FlightCrewsValidator";

import { useFlightCrew, useAddFlightCrew, useUpdateFlightCrew, useDeleteFlightCrew } from "../../../services/flights";
import {
  ComboMultiSelect,
  MaskedText,
} from "../../../controls";


const FlightCrewList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(FlightCrewsValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddFlightCrew = useAddFlightCrew();
  const handleUpdateFlightCrew = useUpdateFlightCrew();
  const handleDeleteFlightCrew = useDeleteFlightCrew();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const flightCrews = useFlightCrew({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };
    const cellStyle = { fontSize: 12, padding: "4px 2px" };
    const headerStyle = { fontSize: 12, padding: "4px 2px" };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, undefined, false),
        title: "Code",
        field: "code",
        editable: state.canEdit? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(4)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(200, 4, undefined, cellStyle, headerStyle, undefined, false),
        title: "First Name",
        field: "firstName",
        editable: state.canEdit? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(200, 4, undefined, cellStyle, headerStyle, undefined, false),
        title: "Middle Name",
        field: "middleName",
        editable: state.canEdit? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(200, 4, undefined, cellStyle, headerStyle, undefined, false),
        title: "Last Name",
        field: "lastName",
        editable: state.canEdit? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Known As",
        field: "fullName",
        editable: state.canEdit? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, 4, undefined, cellStyle, headerStyle, undefined, false),
        title: "Functions",
        field: "functions",
        editable: state.canEdit? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => {
          return (
            <ComboMultiSelect
              value={props.value}
              readOnly={!state.canEdit}
              disabled={false}
              group="CREW"
              subGroup="ROLE"
              renderValue={false}
              fullWidth
              displayOnly={false}
              returnType="STRING"
              style={{marginTop: -10}}
              onChange={(value) => {
                let roleArray = typeof value === "string" && value !== "" ? value.split(",") : [];
                __.remove(roleArray, (s) => s === "");
                const updatedRowData = {...props.rowData, functions: roleArray};
                props.onRowDataChange(updatedRowData);
              }}
            />
          )
        },
        render: (rowData) => {
          const functions = rowData?.functions && Array.isArray(rowData?.functions) && rowData?.functions.length > 0 ? rowData?.functions.join(", ") : "Not Available";
          return (<span>{functions}</span>)
        }
      },

    ];
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 15,
              detailPanelType: "single",
            }}
            components={{
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Flight Crew Management"
            columns={getColumns({canEdit: state.canEdit})}
            data={flightCrews.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddFlightCrew.mutate({record: newData, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateFlightCrew.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteFlightCrew.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default FlightCrewList;
